import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api, { formatApiErrorDetail, isTenantSurface } from "@/lib/api";
import { BrandMark, BrandName } from "@/components/logo/BrandLogo";
import { PronounceButton } from "@/components/PronounceButton";
import CircuitBackground from "@/components/CircuitBackground";
import AnnouncementTicker from "@/components/AnnouncementTicker";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ArrowRight, Building2, Loader2, ShieldCheck } from "lucide-react";

/* Demo panel row labels ONLY — credentials are never shipped in the bundle;
   they are fetched from /api/auth/general/demo at reveal time (Shift+Ctrl+S). */
const DEMO_LABELS = {
  admin: "login.demoAdmin",
  hr: "login.demoHrManager",
  depthead: "login.demoDeptHead",
  employee: "login.demoEmployee",
};

export default function LoginPage() {
  const { user, login } = useAuth();
  const { branding, refresh: refreshBranding } = useBranding();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState(null); // multi-workspace picker
  const [picking, setPicking] = useState(null);
  const [demoPanel, setDemoPanel] = useState(null); // hidden until Shift+Ctrl+S
  const [demoWorkspace, setDemoWorkspace] = useState(null); // demo rows land in vertex
  const submitRef = useRef(null);

  // Secret reveal: Shift+Ctrl+S toggles the demo panel; Escape hides it.
  // Credentials are fetched ONLY at reveal time — nothing demo-related is in
  // the DOM (or discoverable in the React tree) until the hotkey is pressed.
  useEffect(() => {
    if (isTenantSurface()) return undefined; // never on white-label surfaces
    const onKey = (e) => {
      if (e.key === "Escape") { setDemoPanel(null); return; }
      const isS = (e.key || "").toLowerCase() === "s" || e.code === "KeyS";
      if (!(e.ctrlKey && e.shiftKey && isS)) return;
      e.preventDefault();
      if (demoPanel) { setDemoPanel(null); return; }
      api.get("/auth/general/demo")
        .then((r) => {
          if (r.data?.available && r.data.accounts?.length) setDemoPanel(r.data);
        })
        .catch(() => { /* not available -> stays hidden */ });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [demoPanel]);

  if (user) return <Navigate to="/" replace />;

  const afterLogin = (data) => {
    if (data?.status === "choose_workspace") { setWorkspaces(data.workspaces); return; }
    if (data?.account_type === "platform") { window.location.href = "/platform"; return; }
    refreshBranding(); // base-domain login resolved a workspace -> re-theme
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // demoWorkspace pins the demo flow to vertex — one step, no picker
      afterLogin(await login(email, password, remember, demoWorkspace));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (acct) => {
    setEmail(acct.email);
    setPassword(acct.password);
    setDemoWorkspace("vertex");
    setError("");
    submitRef.current?.focus(); // do NOT auto-submit
  };

  const chooseWorkspace = async (ws) => {
    const key = ws.type === "platform" ? "platform" : ws.subdomain;
    setError("");
    setPicking(key);
    try {
      afterLogin(await login(email, password, remember, key));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPicking(null);
    }
  };

  const baseSurface = !isTenantSurface(); // base domain -> landing exists
  const product = branding?.product_name || "Aetria OS";
  const showDemoPanel = baseSurface && !workspaces && Boolean(demoPanel);

  return (
    <div className="min-h-screen flex flex-col relative"
      style={{ background: "var(--brand-primary, #1E293B)" }} data-testid="login-page">
      <CircuitBackground />

      {/* top header — same bones as the landing nav */}
      <nav className="sticky top-0 z-20 border-b border-white/10 backdrop-blur"
        style={{ background: "color-mix(in srgb, var(--brand-primary, #1E293B) 95%, transparent)" }}
        data-testid="login-nav">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2.5" data-testid="login-nav-brand">
            <BrandMark size={30} inverted />
            <span className="text-white font-bold text-[16px] tracking-tight">
              <BrandName />
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector dark />
            {baseSurface && (
              <Link to="/" data-testid="login-back-home"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-300 hover:text-white transition-colors">
                <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {t("login.backToHome")}
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* centered card over the live circuit */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-12">
        <div className={`w-full ${showDemoPanel ? "max-w-4xl" : "max-w-md"}`}>
          <div className="mb-8 flex items-center gap-4" data-testid="login-brand-header">
            <BrandMark size={48} inverted />
            <div>
              <div className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                <BrandName />
                {baseSurface && <PronounceButton dark testid="login-pronounce-btn" />}
              </div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-1">
                {branding?.org_name || t("login.workforceIntelligence")}
              </p>
            </div>
          </div>
          <div className={showDemoPanel
            ? "grid gap-6 lg:grid-cols-[minmax(0,28rem)_minmax(0,21rem)] items-start"
            : ""}>
          <div className="w-full">
          {workspaces ? (
          <div className="bg-white/95 backdrop-blur-md border border-white/20 p-8 space-y-5 shadow-2xl"
            data-testid="workspace-picker">
            <div>
              <h2 className="text-[15px] font-bold text-slate-900">{t("login.chooseWorkspace")}</h2>
              <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">
                {t("login.chooseWorkspaceHint")}
              </p>
            </div>
            {error && (
              <p className="text-[13px] text-red-600 border border-red-200 bg-red-50 px-3 py-2"
                data-testid="workspace-picker-error">
                {error}
              </p>
            )}
            <div className="space-y-2">
              {workspaces.map((ws) => {
                const key = ws.type === "platform" ? "platform" : ws.subdomain;
                const blocked = Boolean(ws.blocked);
                return (
                  <button key={key} type="button" disabled={blocked || picking !== null}
                    data-testid={`workspace-option-${key}`}
                    onClick={() => chooseWorkspace(ws)}
                    className="w-full flex items-center gap-3 border border-slate-200 px-4 py-3 text-start transition-colors hover:border-teal-600 hover:bg-teal-50/40 disabled:cursor-not-allowed disabled:opacity-55">
                    <span className="h-9 w-9 flex items-center justify-center bg-[var(--brand-primary)] text-white shrink-0">
                      {ws.type === "platform"
                        ? <ShieldCheck className="h-4 w-4" />
                        : <Building2 className="h-4 w-4" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-semibold text-slate-900 truncate">
                        {ws.type === "platform" ? t("login.platformConsole") : ws.org_name || ws.subdomain}
                      </span>
                      <span className="block text-[11px] text-muted-foreground truncate">
                        {ws.type === "platform"
                          ? t("login.platformConsoleDesc")
                          : <>{ws.subdomain} · {t(`roles.${ws.role}`, ws.role)}</>}
                      </span>
                    </span>
                    {blocked ? (
                      <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 shrink-0">
                        {ws.code === "tenant_pending" ? t("login.workspacePending") : t("login.workspaceSuspended")}
                      </span>
                    ) : picking === key
                      ? <Loader2 className="h-4 w-4 animate-spin text-slate-400 shrink-0" />
                      : <ArrowRight className="h-4 w-4 text-slate-300 rtl:rotate-180 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <button type="button" data-testid="workspace-picker-back"
              onClick={() => { setWorkspaces(null); setError(""); }}
              className="text-[12px] text-slate-500 hover:text-slate-800 hover:underline underline-offset-2">
              {t("login.useDifferentAccount")}
            </button>
          </div>
          ) : (
          <form onSubmit={submit}
            className="bg-white/95 backdrop-blur-md border border-white/20 p-8 space-y-5 shadow-2xl">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[12px] uppercase tracking-[0.1em] text-muted-foreground">
                {t("login.workEmail")}
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setDemoWorkspace(null); }}
                placeholder="you@company.com"
                className="rounded-none h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12px] uppercase tracking-[0.1em] text-muted-foreground">
                {t("login.password")}
              </Label>
              <PasswordInput
                id="password"
                data-testid="login-password-input"
                required
                value={password}
                onChange={(e) => { setPassword(e.target.value); setDemoWorkspace(null); }}
                placeholder="••••••••"
                className="rounded-none h-10"
              />
            </div>
            {error && (
              <p className="text-[13px] text-red-600 border border-red-200 bg-red-50 px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="login-remember-label">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(Boolean(v))}
                data-testid="login-remember-checkbox" className="rounded-none" />
              <span className="text-[12px] text-slate-600 leading-tight">
                {t("login.rememberDevice")}
              </span>
            </label>
            <Button
              type="submit"
              ref={submitRef}
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full rounded-none bg-[var(--brand-primary)] hover:bg-slate-700 text-white h-10 font-medium"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.signIn")}
            </Button>
          </form>
          )}
          {branding?.login_announcements?.enabled
            && branding.login_announcements.lines?.length > 0 && (
            <AnnouncementTicker lines={branding.login_announcements.lines} className="mt-5" />
          )}
          </div>

          {/* Base-login only: demo workspace panel (right of card / below on mobile).
              Gated server-side — renders only when the vertex demo tenant exists. */}
          {showDemoPanel && (
          <aside data-testid="login-demo-panel"
            className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl">
            <div className="px-5 pt-5 pb-4 border-b border-slate-200">
              <p className="text-[11px] uppercase tracking-[0.14em] text-slate-900 font-semibold">
                {t("login.exploreDemo")}
              </p>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                {t("login.demoCaption")}
              </p>
            </div>
            <div className="p-3 space-y-1.5">
              {demoPanel.accounts.map((a) => (
                <button key={a.key} type="button" data-testid={`demo-credential-${a.key}`}
                  onClick={() => fillDemo(a)}
                  className="group w-full flex items-center justify-between gap-3 border border-slate-200 bg-white px-3.5 py-2.5 text-start transition-colors hover:border-[var(--brand-accent-ink)]">
                  <span className="min-w-0">
                    <span className="block text-[12px] font-semibold text-slate-900">
                      {t(DEMO_LABELS[a.key] || a.key)}
                    </span>
                    <span className="block text-[11px] font-mono text-slate-500 truncate">
                      {a.email}
                    </span>
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-slate-400 group-hover:text-[var(--brand-accent-ink)] transition-colors shrink-0">
                    {t("login.demoUse")}
                  </span>
                </button>
              ))}
            </div>
          </aside>
          )}
          </div>
        </div>
      </main>

      {/* footer strip — consistent with the landing footer */}
      <footer className="relative z-10 border-t border-white/10 py-5" data-testid="login-footer">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] text-slate-400 font-mono">
            {product.toUpperCase()} — {t("login.footerTagline")}
          </p>
          {baseSurface && (
            <Link to="/signup" className="text-[11px] hover:underline"
              style={{ color: "var(--brand-accent-bright)" }} data-testid="login-footer-signup">
              {t("login.requestTrial")}
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
}
